import { useMemo } from 'react';

import { useContentfulContentTypeEntriesFactory } from '@sg/shared-fe-integrations-contentful';

import type { ContentfulClientApi } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useSharedMenuContent = (params: Params) => {
  const { contentfulClient, isPreviewModeEnabled, pause } = params;

  const { data, fetching: isFetching } =
    useContentfulContentTypeEntriesFactory<SharedMenuContentFields>({
      contentfulClient,
      isPreviewModeEnabled,
      contentType: CONTENT_TYPE,
      pause,
    });

  const fields = data?.[0]?.fields;

  const mappedContent = useMemo<MappedSharedMenuContent>(() => {
    return {
      categoriesBadges: parseCategoriesBadges(fields?.categoriesBadges),
      caloriesOverrides: parseCaloriesOverrides(fields?.caloriesOverrides),
      denseCategoryIds: new Set(fields?.denseCategories),
      caloriesOnlyCategoryIds: new Set(fields?.caloriesOnlyCategories),
    };
  }, [fields]);

  return {
    mappedContent,
    isFetching,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseCategoriesBadges(entries: readonly string[] | undefined): CategoriesBadges {
  if (!entries) return {};

  return Object.fromEntries(
    entries.filter((entry) => CATEGORY_BADGE_PATTERN.test(entry)).map(parseCategoryBadge),
  );
}

function parseCategoryBadge(entry: string): [string, string] {
  const [id, badge] = entry.split('-');

  return [id, badge];
}

function parseCaloriesOverrides(rawEntries: unknown): CaloriesOverrides {
  if (!rawEntries || typeof rawEntries !== 'object') return {};

  const parsedEntries = Object.entries(rawEntries)
    .map(parseCaloriesOverrideEntry)
    .filter((entry): entry is CaloriesOverrideEntry => Boolean(entry));

  return Object.fromEntries(parsedEntries);
}

function parseCaloriesOverrideEntry([slug, rawEntry]: [unknown, unknown]):
  | CaloriesOverrideEntry
  | undefined {
  const hasInvalidEntry = typeof slug !== 'string' || !rawEntry || typeof rawEntry !== 'object';

  if (hasInvalidEntry) return;

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const record = rawEntry as Record<string, unknown>;

  const override = record.caloriesOverride;
  const overrideText = record.caloriesOverrideText;

  const hasValidOverride = typeof override === 'number' && !Number.isNaN(override);
  const hasValidOverrideText = typeof overrideText === 'string';

  const caloriesOverride = hasValidOverride ? override : undefined;
  const caloriesOverrideText = hasValidOverrideText ? overrideText : undefined;

  if (caloriesOverride === undefined && caloriesOverrideText === undefined) {
    return;
  }

  const parsedEntry: CaloriesOverrideData = { caloriesOverride, caloriesOverrideText };

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- narrowed by hasInvalidEntry guard
  return [slug, parsedEntry];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPE = 'orderMenuScreenContent';
const CATEGORY_BADGE_PATTERN = /^\d+-[A-Za-z0-9 ]+$/;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  contentfulClient: ContentfulClientApi | undefined;
  isPreviewModeEnabled?: boolean;
  pause?: boolean;
};

type SharedMenuContentFields = {
  categoriesBadges?: string[];
  caloriesOverrides?: CaloriesOverrides;
  denseCategories?: string[];
  caloriesOnlyCategories?: string[];
};

type MappedSharedMenuContent = {
  categoriesBadges: CategoriesBadges;
  caloriesOverrides: CaloriesOverrides;
  denseCategoryIds: Set<string>;
  caloriesOnlyCategoryIds: Set<string>;
};

type CategoriesBadges = Record<string, string>;

type CaloriesOverrides = Record<string, CaloriesOverrideData>;

type CaloriesOverrideData = {
  caloriesOverride?: number;
  caloriesOverrideText?: string;
};

type CaloriesOverrideEntry = [string, CaloriesOverrideData];
